import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSquareArrowUpRight } from '@fortawesome/free-solid-svg-icons';
import cacheManager from '../CacheManager';
import { formatDate, formatTime } from '../js/dateFormatter';
import { showSuccess, showError } from '../utils/toast';

const PgAgendamentos = () => {
  
  const [clientesByIdCache, setClientesByIdCache] = useState({});
  const [itensByIdCache, setItensByIdCache] = useState({});
  const [espacosByIdCache, setEspacosByIdCache] = useState({});
  
  const [agendamentos, setAgendamentos] = useState([]);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const agendamentosPorPagina = 10;
  const [termoDeBusca, setTermoDeBusca] = useState('');
  const [agendamentosFiltrados, setAgendamentosFiltrados] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const navigate = useNavigate();

  const search = async () => {
    try {
      setIsLoading(true);

      const espacosByIdCache = await cacheManager.getEspacosById()
      setEspacosByIdCache(espacosByIdCache);
      const clientesByIdCache = await cacheManager.getClientesById()
      setClientesByIdCache(clientesByIdCache);
      const itensByIdCache = await cacheManager.getItensById()
      setItensByIdCache(itensByIdCache);
      const agendamentosCache = await cacheManager.getAgendamentosCache();
      setAgendamentos(agendamentosCache);

    } catch (error) {
      console.error('Erro ao buscar agendamentos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFaturarNaoPago = async (agendamento) => { 
    try {
      setIsLoading(true);
      const response = await fetch(`https://api.faeworks.com.br/acoes/agendamentoFaturarNaoPago.php?id=${agendamento.id_agendamento}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setIsLoading(false);
      cacheManager.invalidateAgendamentosCache();
      cacheManager.invalidateFaturasCache();
      if (response.ok) {
        const data = await response.json();
        console.log('Faturamento realizado com sucesso:', data);
        showSuccess('Faturamento realizado com sucesso!');
        recarregarComponente();
      } else {
        console.error('Erro ao realizar o faturamento:', response.statusText);
        showError('Erro ao realizar o faturamento. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao realizar o faturamento:', error);
      showError('Erro ao realizar o faturamento. Por favor, tente novamente.');
    }
  }
  const handleFaturarPago = async (agendamento) => { 
    try {
      setIsLoading(true);
      const response = await fetch(`https://api.faeworks.com.br/acoes/agendamentoFaturarPago.php?id=${agendamento.id_agendamento}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setIsLoading(false);
      cacheManager.invalidateAgendamentosCache();
      cacheManager.invalidateFaturasCache();
      cacheManager.invalidatePagamentosCache();
      if (response.ok) {
        const data = await response.json();
        console.log('Faturamento realizado com sucesso:', data);
        showSuccess('Faturamento realizado com sucesso!');
        recarregarComponente();
      } else {
        console.error('Erro ao realizar o faturamento:', response.statusText);
        showError('Erro ao realizar o faturamento. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao realizar o faturamento:', error);
      showError('Erro ao realizar o faturamento. Por favor, tente novamente.');
    }
  }

  // Estilos para esmaecer a página
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2, // Garanta que esteja acima de outros elementos da página
    display: isLoading ? 'block' : 'none', // Mostra apenas quando está carregando
  };

  useEffect(() => {
    search();
  }, [reload]);

  const recarregarComponente = () => {
    setReload(prev => !prev); // Alterna o valor de 'reload' para forçar recarregamento
  };

  useEffect(() => {
    const agendamentosOrdenados = [...agendamentos].sort((a, b) => {
      const dataA = new Date(a.data_inicio_agendamento);
      const dataB = new Date(b.data_inicio_agendamento);

      return dataB - dataA;
    });

    const agendamentosFiltrados = agendamentosOrdenados.filter(agendamento => {
      let tituloItem = (itensByIdCache[agendamento.id_item]?.titulo || '')
      tituloItem = tituloItem.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
      let nomeCliente = clientesByIdCache[agendamento.id_cliente]?.nome || ''
      nomeCliente = nomeCliente.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
      return tituloItem.includes(termoDeBusca.toLowerCase()) || nomeCliente.includes(termoDeBusca.toLowerCase());
    });

    setAgendamentosFiltrados(agendamentosFiltrados);
    setPaginaAtual(1);
  }, [agendamentos, termoDeBusca, clientesByIdCache, itensByIdCache]);

  const addAgendamento = () => {
    navigate('/agendamento/novo'); // Use navigate para ir para /agendamento/novo
  };

  // Calcular o número total de páginas
  const totalPaginas = Math.ceil(agendamentosFiltrados.length / agendamentosPorPagina);

  // Obter os agendamentos da página atual
  const indexDoUltimoAgendamento = paginaAtual * agendamentosPorPagina;
  const indexDoPrimeiroAgendamento = indexDoUltimoAgendamento - agendamentosPorPagina;
  const agendamentosAtuais = agendamentosFiltrados.slice(indexDoPrimeiroAgendamento, indexDoUltimoAgendamento);

  // Funções para alterar a página
  const irParaPagina = (numero) => {
    setPaginaAtual(numero);
  };

  return (
    <div className="container mt-5">
      <style>
        {`
          .nowrap-column {
            max-width: 240px; /* Adjust the width as needed */
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        `}
      </style>
      {isLoading && <div style={overlayStyle}></div>} {/* Overlay de carregamento */}
      <h2 className="mb-4">Agendamentos</h2>
      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar..."
          id="search-input"
          value={termoDeBusca}
          onChange={(e) => setTermoDeBusca(e.target.value)}
        />
        <button className="btn btn-outline-secondary" type="button" onClick={search}>Buscar</button>
      </div>
      <table className="table">
        <thead className="table-light">
          <tr>
            <th>Título</th>
            <th>Fatura</th>
            <th>Cliente</th>
            <th>Data</th>
            <th>Início</th>
            <th>Fim</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {agendamentosAtuais.map((agendamento, index) => (
            <tr key={index} style={{ height: 'auto !important', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              
              <td className="nowrap-column">
                <Link to={`/agendamento/editar/${agendamento.id_agendamento}`} className="text-dark underline-on-hover">
                  {itensByIdCache[agendamento.id_item]?.titulo || 'Item não encontrado'}
                </Link>
              </td>
              <td>
                {(agendamento.id_fatura!=null && agendamento.id_fatura !== "0") &&
                <Link to={`/fatura/editar/${agendamento.id_fatura}`} className="text-dark underline-on-hover">
                  {agendamento.id_fatura}
                </Link>
                }
                

                {(agendamento.id_fatura==null || agendamento.id_fatura === "0") &&
                <div className="dropdown d-inline">
                  <button className="btn btn-secondary btn-sm dropdown-toggle" type="button" id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                    <FontAwesomeIcon icon={faSquareArrowUpRight} /> Faturar
                  </button>
                  <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                    <li><button className="dropdown-item" onClick={() => handleFaturarNaoPago(agendamento)}>Em aberto</button></li>
                    {agendamento.id_fatura && (
                      <li><button className="dropdown-item" onClick={() => handleFaturarPago(agendamento)}>Paga</button></li>
                    )}
                  </ul>
                </div>
                }
              </td>
              <td className="nowrap-column">{clientesByIdCache[agendamento.id_cliente]?.nome || 'Cliente não encontrado'}</td>
              <td>{formatDate(agendamento.data_inicio_agendamento)}</td>
              <td>{formatTime(agendamento.data_inicio_agendamento)}</td>
              <td>{formatTime(agendamento.data_fim_agendamento)}</td>
              <td>
                <Link to={`/agendamento/editar/${agendamento.id_agendamento}`} className="btn btn-primary btn-sm me-2">
                  <FontAwesomeIcon icon={faEdit} /> Editar
                </Link>
                <button className="btn btn-danger btn-sm" disabled>
                  <FontAwesomeIcon icon={faTrash} /> Excluir
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          {/* Conteúdo da paginação */}
          <button onClick={() => irParaPagina(1)} disabled={paginaAtual === 1} className="btn btn-secondary" style={{ fontSize: '10px' }}>
            Primeira
          </button>
          {paginaAtual > 2 && (
            <button onClick={() => irParaPagina(paginaAtual - 2)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual - 2}
            </button>
          )}
          {paginaAtual > 1 && (
            <button onClick={() => irParaPagina(paginaAtual - 1)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual - 1}
            </button>
          )}
          <button className="btn btn-primary" style={{ fontSize: '10px' }}>
            {paginaAtual}
          </button>
          {paginaAtual < totalPaginas && (
            <button onClick={() => irParaPagina(paginaAtual + 1)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual + 1}
            </button>
          )}
          {paginaAtual < totalPaginas - 1 && (
            <button onClick={() => irParaPagina(paginaAtual + 2)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual + 2}
            </button>
          )}
          <button onClick={() => irParaPagina(totalPaginas)} disabled={paginaAtual === totalPaginas} className="btn btn-secondary" style={{ fontSize: '10px' }}>
            Última
          </button>
        </div>
        <div>
          <button type="button" className="btn btn-primary" onClick={addAgendamento}>Incluir</button>
        </div>
      </div>
    </div>
  );
};

export default PgAgendamentos;